import { FC } from 'react';

import {
  ImageWithTextSideBySideSlice,
  ImageWithTextSideBySideSliceWithCtaOnImageSide,
} from 'prismicio-types';

import classNames from 'classnames';

import { useFilteredSliceItemsByLocationSlug } from '@/hooks';
import { Heading, PrismicImageWithAlt } from '@/components';
import { resolveImageRadiusToken } from '../../../theme/tokens';
import { getContentVerticalAlign } from '@/utils';
import { CtaSection, textAlign } from '../ImageWithTextSideBySide.components';

import style from './withctaonimagesidevariation.module.scss';

export const WithCtaOnImageSideVariation: FC<{
  slice: ImageWithTextSideBySideSliceWithCtaOnImageSide &
    ImageWithTextSideBySideSlice;
}> = ({ slice }) => {
  const { filteredItems } = useFilteredSliceItemsByLocationSlug(slice?.items);
  const {
    content_horizontal_align,
    content_full_width,
    content_vertical_align,
    cta_description,
    image,
    image_corner_preset,
    item_description_color,
    item_title_color,
    layout_direction,
  } = slice.primary;

  const alignText = content_horizontal_align ?? 'center';
  const layoutDirection = layout_direction ?? 'left';

  const bodyVariables = {
    '--content-padding-left': layoutDirection === 'left' ? '13.6%' : '24px',
    '--content-padding-right': layoutDirection === 'left' ? '24px' : '13.6%',
  };

  if (!filteredItems?.length) {
    return null;
  }

  return (
    <>
      <div
        className={classNames(style.content__item_list, {
          [style.content__item_list__left]: layoutDirection === 'left',
        })}
        style={{
          alignItems: textAlign(content_horizontal_align as string),
          textAlign: alignText,
          alignSelf: getContentVerticalAlign(content_vertical_align),
          ...bodyVariables,
        }}
      >
        {filteredItems?.map((option, index) => (
          <div className={style.content__option_item} key={index}>
            {option.title && (
              <Heading
                className={style.title}
                level={5}
                style={{ color: item_title_color as string }}
              >
                {option.title}
              </Heading>
            )}
            {option.description && (
              <p
                className={style.description}
                style={{ color: item_description_color as string }}
              >
                {option.description}
              </p>
            )}
          </div>
        ))}
      </div>
      <div
        className={classNames(style.figure_container, {
          [style.figure_container__left]: layoutDirection === 'left',
        })}
      >
        <figure
          className={classNames(
            style.figure,
            {
              [style.figure_full_width_left]:
                !!content_full_width && layoutDirection === 'left',
              [style.figure_full_width_right]:
                !!content_full_width && layoutDirection === 'right',
            },
            resolveImageRadiusToken(image_corner_preset as string),
          )}
        >
          <PrismicImageWithAlt
            field={image}
            fill
            showPlaceholderIfEmpty
            style={{ objectFit: 'cover' }}
          />
        </figure>
        <CtaSection slice={slice} />
        {cta_description && (
          <p
            className={classNames(style.description, {
              [style.description_full_width_centered]: !!content_full_width,
            })}
            style={{ color: item_description_color as string }}
          >
            {cta_description}
          </p>
        )}
      </div>
    </>
  );
};
